import React, { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './videoCard.scss';
import { VideoSource } from '../../types';
import TabNavigationHelper from '../../helper/tabNavigationHelper';

export type VideoLoadingMode = 'eager' | 'external' | 'on-play';
export type VideoPreloadMode = 'metadata' | 'auto';

export interface VideoCardProps {
    srcSet: VideoSource[];
    poster?: string;
    ariaLabel?: string;
    autoPlay?: boolean;
    loop?: boolean;
    debug?: boolean;
    has3DView?: boolean;
    isFullScreen?: boolean;
    className?: string;

    /** Eager: sources immediately. External: wait for startLoading. On-play: wait for Play. */
    loadingMode?: VideoLoadingMode;

    /** Hint used after sources are attached. "auto" is not a guarantee of a full download. */
    preloadMode?: VideoPreloadMode;

    /** External-mode trigger; kept for compatibility with existing callers. */
    startLoading?: boolean;
}

const VideoCard: React.FC<VideoCardProps> = ({
    srcSet,
    poster,
    ariaLabel,
    autoPlay = false,
    loop = false,
    debug = false,
    has3DView = false,
    isFullScreen = false,
    className,
    loadingMode = 'eager',
    preloadMode = 'metadata',
    startLoading = false,
}) => {
    const tabNavigation = TabNavigationHelper.instance;
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const playAfterSourcesAttachRef = useRef(false);

    const initiallyActivated =
        loadingMode === 'eager' ||
        autoPlay ||
        (loadingMode === 'external' && startLoading);

    const [sourcesActivated, setSourcesActivated] =
        useState<boolean>(initiallyActivated);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [isControlsVisible, setIsControlsVisible] = useState<boolean>(false);
    const [statusMessage, setStatusMessage] = useState<string>('Paused');

    const sortedSrcSet = useMemo<VideoSource[]>(() => {
        const widthSources = srcSet
            .filter(
                (source): source is VideoSource & { maxWidthPx: number } =>
                    typeof source.maxWidthPx === 'number',
            )
            .sort((a, b) => a.maxWidthPx - b.maxWidthPx);

        // Keep unbounded sources last. With no media condition, they provide
        // a fallback at widths not matched by the preceding sources.
        const fallbackSources = srcSet.filter(
            (source) => typeof source.maxWidthPx !== 'number',
        );

        return [...widthSources, ...fallbackSources];
    }, [srcSet]);

    const externalTrigger =
        loadingMode === 'external' && startLoading;

    const shouldRenderSources =
        loadingMode === 'eager' ||
        autoPlay ||
        sourcesActivated ||
        externalTrigger;

    // Latch activation so sources stay attached if startLoading later becomes false.
    useEffect(() => {
        if (shouldRenderSources && !sourcesActivated) {
            setSourcesActivated(true);
        }
    }, [shouldRenderSources, sourcesActivated]);

    // In on-play mode, attach sources on the first Play action, then play
    // once React has committed those source elements.
    useEffect(() => {
        const video = videoRef.current;

        if (
            !video ||
            !shouldRenderSources ||
            !playAfterSourcesAttachRef.current
        ) {
            return;
        }

        playAfterSourcesAttachRef.current = false;

        void video.play().catch(() => {
            setStatusMessage('Tap again to start the video.');
        });
    }, [shouldRenderSources]);

    const play3DView = React.useCallback(
        (play3d = false) => {
            if (has3DView) {
                window.shell.publishTo('SCS.PlayPauseAnimation', {
                    containerId: 'ThreeSixtyView_mixedMedia',
                    play3d,
                });
            }
        },
        [has3DView],
    );

    const clearControlsTimer = (): void => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    };

    const hideControls = (): void => {
        setIsControlsVisible(false);
        clearControlsTimer();
    };

    const showPlayingControlTemporarily = (): void => {
        setIsControlsVisible(true);
        clearControlsTimer();

        hideTimerRef.current = setTimeout(() => {
            const video = videoRef.current;

            if (video && !video.paused && !video.ended) {
                setIsControlsVisible(false);
            }
        }, 1400);
    };

    const togglePlayback = async (
        event?: MouseEvent<HTMLElement>,
    ): Promise<void> => {
        event?.stopPropagation();

        const video = videoRef.current;
        if (!video || isError) return;

        if (video.paused || video.ended) {
            if (!shouldRenderSources) {
                if (loadingMode === 'on-play') {
                    playAfterSourcesAttachRef.current = true;
                    setSourcesActivated(true);
                } else {
                    setStatusMessage('Video is not ready to play yet.');
                }
                return;
            }

            try {
                await video.play();
            } catch {
                setStatusMessage('Tap again to start the video.');
            }
        } else {
            video.pause();
        }
    };

    const handlePlay = (): void => {
        setIsPlaying(true);
        setIsError(false);
        setStatusMessage(`Playing: ${videoRef.current?.currentSrc}`);
        showPlayingControlTemporarily();
        play3DView(false);
    };

    const handlePause = (): void => {
        setIsPlaying(false);
        hideControls();
        setStatusMessage(`Paused: ${videoRef.current?.currentSrc}`);
        play3DView(true);
    };

    const handleEnded = (): void => {
        setIsPlaying(false);
        hideControls();
        setStatusMessage(`Paused: ${videoRef.current?.currentSrc}`);
    };

    const handleVideoError: React.ReactEventHandler<HTMLVideoElement> = (
        event,
    ) => {
        const video = event.currentTarget;
        const error = video.error;

        // Ignore source-child events and events without a media-element error.
        if (event.target !== video || !error) return;

        setIsError(true);
        setIsPlaying(false);
        setIsControlsVisible(false);
        setStatusMessage(
            `Video could not be loaded: ${video.currentSrc || 'no source selected'}\n` +
                `Error: ${error.code} ${error.message}`,
        );
    };

    // Autoplay is an explicit request to load and play, regardless of mode.
    useEffect(() => {
        if (!autoPlay) return;

        const video = videoRef.current;
        if (!video) return;

        play3DView(false);

        void video.play().catch(() => {
            setStatusMessage('Tap to start the video.');
            play3DView(true);
        });
    }, [autoPlay, play3DView]);

    useEffect(() => {
        return () => {
            clearControlsTimer();
        };
    }, []);

    return (
        <div className={classNames(styles.video_card_wrapper, className)}>
            <div
                className={classNames(styles.video_component, {
                    [styles.is_playing]: isPlaying,
                    [styles.is_controls_visible]:
                        !isError && isControlsVisible,
                    [styles.is_fullscreen]: isFullScreen,
                })}
                onClick={togglePlayback}
                aria-label={isPlaying ? 'Pause video' : 'Play video'}
                ref={(el) => tabNavigation.setFocusAtt(el)}
            >
                <video
                    ref={videoRef}
                    preload={shouldRenderSources ? preloadMode : 'none'}
                    poster={poster}
                    autoPlay={autoPlay}
                    loop={loop}
                    muted
                    playsInline
                    disablePictureInPicture
                    controlsList="nodownload noremoteplayback noplaybackrate"
                    aria-label={ariaLabel}
                    onPlay={handlePlay}
                    onPause={handlePause}
                    onEnded={handleEnded}
                    onError={handleVideoError}
                >
                    {shouldRenderSources &&
                        sortedSrcSet.map((source, index) => (
                            <source
                                key={`${source.src}-${index}`}
                                src={source.src}
                                type={
                                    source.type
                                        ? `video/${source.type}`
                                        : undefined
                                }
                                media={
                                    typeof source.maxWidthPx === 'number'
                                        ? `(max-width: ${source.maxWidthPx}px)`
                                        : undefined
                                }
                            />
                        ))}
                    Your browser does not support HTML video.
                </video>

                {!isError && (
                    <button
                        className={classNames(styles.play_toggle, {
                            [styles.is_playing]: isPlaying,
                        })}
                        type="button"
                        aria-label={isPlaying ? 'Pause video' : 'Play video'}
                        onClick={togglePlayback}
                    >
                        <span className={styles.icon_play} aria-hidden="true" />
                        <span
                            className={styles.icon_pause}
                            aria-hidden="true"
                        />
                    </button>
                )}
            </div>

            {debug && (
                <p
                    data-testid="video-status"
                    className={styles.status}
                    aria-live="polite"
                >
                    {statusMessage}
                </p>
            )}
        </div>
    );
};

export default VideoCard;